// Import a call list from a CSV. Every caller works the same shared pool.

import { useMemo, useState } from 'react';
import { importCallRows, type ImportCounts } from '../../lib/data';
import { CALL_FIELDS, guessColumns, parseCsv, toCallRows, type CallFieldId } from '../../lib/csv';
import { CloseX, ErrorBox, useDrawer, useToast } from '../../ui/kit';

const CHUNK = 400;

const sum = (a: ImportCounts, b: ImportCounts): ImportCounts => ({
  total: a.total + b.total,
  imported: a.imported + b.imported,
  duplicate: a.duplicate + b.duplicate,
  city_blocked: a.city_blocked + b.city_blocked,
  bad_phone: a.bad_phone + b.bad_phone,
  blacklisted: a.blacklisted + b.blacklisted,
  list_id: b.list_id ?? a.list_id,
});
const ZERO: ImportCounts = {
  total: 0,
  imported: 0,
  duplicate: 0,
  city_blocked: 0,
  bad_phone: 0,
  blacklisted: 0,
  list_id: null,
};

export function ImportDrawer() {
  const toast = useToast();
  const { close } = useDrawer();
  const [table, setTable] = useState<string[][] | null>(null);
  const [name, setName] = useState('');
  const [market, setMarket] = useState<'TR' | 'UK'>('TR');
  const [map, setMap] = useState<Record<CallFieldId, number> | null>(null);
  const [check, setCheck] = useState<ImportCounts | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const rows = useMemo(() => (table && map ? toCallRows(table, map) : []), [table, map]);

  async function onFile(f: File | undefined) {
    if (!f) return;
    setError(null);
    setCheck(null);
    const t = parseCsv(await f.text());
    if (t.length < 2) {
      setError(new Error('The file has no rows. Export the sheet as CSV and try again.'));
      return;
    }
    setTable(t);
    setMap(guessColumns(t[0]!));
    setName(f.name.replace(/\.[^.]+$/, ''));
  }

  async function run(dry: boolean): Promise<ImportCounts> {
    let acc = ZERO;
    let list: string | null = null;
    for (let i = 0; i < rows.length; i += CHUNK) {
      setBusy(
        `${dry ? 'Checking' : 'Importing'} ${Math.min(i + CHUNK, rows.length)} of ${rows.length}`,
      );
      const r = await importCallRows({
        name,
        market,
        rows: rows.slice(i, i + CHUNK),
        list,
        caller: null,
        dry,
      });
      list = r.list_id;
      acc = sum(acc, r);
    }
    return { ...acc, list_id: list };
  }

  async function doCheck() {
    setError(null);
    try {
      setCheck(await run(true));
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  }

  async function doImport() {
    setError(null);
    try {
      const r = await run(false);
      toast(`${r.imported} contacts imported`);
      close();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  }

  const headers = table?.[0] ?? [];
  const ready = rows.length > 0 && map !== null && map.phone >= 0 && map.clinic >= 0 && name.trim();

  return (
    <>
      <div className="dhead">
        <div>
          <h2 id="dtitle">Import call list</h2>
          <div className="sm">CSV from Google Sheets: File, Download, Comma-separated values.</div>
        </div>
        <CloseX />
      </div>
      <div className="dbody">
        <label className="field">
          <span>File</span>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => void onFile(e.target.files?.[0])}
          />
        </label>

        {table && map && (
          <>
            <div className="blk" style={{ marginTop: 22 }}>
              <h3>
                <span className="lbl g">Match columns</span>
              </h3>
              <div className="panel grid2">
                {CALL_FIELDS.map((f) => (
                  <label className="field" key={f.id}>
                    <span>{f.label}</span>
                    <select
                      value={map[f.id]}
                      onChange={(e) => {
                        setCheck(null);
                        setMap({ ...map, [f.id]: Number(e.target.value) });
                      }}
                    >
                      <option value={-1}>Ignore</option>
                      {headers.map((h, i) => (
                        <option key={i} value={i}>
                          {h || `Column ${i + 1}`}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
            </div>

            <div className="blk grid2">
              <label className="field">
                <span>List name</span>
                <input value={name} onChange={(e) => setName(e.target.value)} />
              </label>
              <label className="field">
                <span>Market</span>
                <select value={market} onChange={(e) => setMarket(e.target.value as 'TR' | 'UK')}>
                  <option>TR</option>
                  <option>UK</option>
                </select>
              </label>
            </div>

            {check && (
              <div className="blk">
                <h3>
                  <span className="lbl p">Checks</span>
                </h3>
                <div className="panel">
                  <div className="minirow">
                    <span className="sdot ok" />
                    <div className="grow">{check.imported} contacts ready to call</div>
                  </div>
                  <div className="minirow">
                    <span className="sdot warn" />
                    <div className="grow">
                      {check.duplicate} already in a list or the pipeline, skipped
                    </div>
                  </div>
                  <div className="minirow">
                    <span className="sdot warn" />
                    <div className="grow">
                      {check.city_blocked} in a city where we have a client, skipped
                    </div>
                  </div>
                  <div className="minirow">
                    <span className="sdot warn" />
                    <div className="grow">
                      {check.bad_phone} without a usable phone number, skipped
                    </div>
                  </div>
                  <div className="minirow">
                    <span className="sdot err" />
                    <div className="grow">
                      {check.blacklisted} on the do-not-contact list, skipped
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        <ErrorBox error={error} />
        {busy && (
          <p role="status" style={{ color: 'var(--muted)' }}>
            {busy}…
          </p>
        )}
      </div>
      <div className="dfoot">
        {check ? (
          <button
            className="btn primary big"
            disabled={!!busy || check.imported === 0}
            onClick={() => void doImport()}
          >
            Import {check.imported} contacts
          </button>
        ) : (
          <button
            className="btn primary big"
            disabled={!ready || !!busy}
            onClick={() => void doCheck()}
          >
            Check {rows.length || ''} rows
          </button>
        )}
        <button className="btn ghost" onClick={close}>
          Cancel
        </button>
      </div>
    </>
  );
}
